import React from 'react';
import { Chip, Tooltip, ChipProps } from '@mui/material';
import { UserRole } from '../../features/user-management';
import {
  getRoleDisplayName,
  getRoleDescription,
  getRoleChipVariant,
  getRoleColor,
} from '../utils/roleUtils';

interface RoleChipProps extends Omit<ChipProps, 'label' | 'color'> {
  role: UserRole | string;
  showTooltip?: boolean;
  short?: boolean;
  customLabel?: string;
}

/**
 * A standardized chip component for displaying user roles
 * with consistent styling and optional tooltips
 */
export const RoleChip: React.FC<RoleChipProps> = ({
  role,
  showTooltip = true,
  short = false,
  customLabel,
  size = 'small',
  variant = 'filled',
  ...chipProps
}) => {
  const displayName = customLabel || getRoleDisplayName(role, short);
  const description = getRoleDescription(role);
  const chipVariant = getRoleChipVariant(role);
  const roleColor = getRoleColor(role);

  const chip = (
    <Chip
      label={displayName}
      color={chipVariant}
      size={size}
      variant={variant}
      sx={{
        fontWeight: 500,
        // Custom color override if needed
        ...(chipVariant === 'default' && {
          backgroundColor: roleColor,
          color: '#fff',
          '&:hover': {
            backgroundColor: roleColor,
            filter: 'brightness(0.9)',
          },
        }),
      }}
      {...chipProps}
    />
  );

  if (showTooltip && description) {
    return (
      <Tooltip title={description} arrow placement="top">
        {chip}
      </Tooltip>
    );
  }

  return chip;
};

export default RoleChip;
